import { motion } from "framer-motion";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from "recharts";

export const STATUS_COLORS = {
  "NOT STARTED": "#D8B4E2",
  "IN PROGRESS": "#90C2E7",
  DONE: "#A8E6CF",
  "ON HOLD": "#FFD3B6",
  DEPRIORITIZED: "#FF8B94",
  REVIEW: "#FFC3A0",
};

const DONUT_DATA = Object.entries(STATUS_COLORS).map(([name, color]) => ({ name, value: 1, color }));

const OWNERS = [
  { name: "User 1", value: 1, color: "#90C2E7" },
  { name: "User 2", value: 1, color: "#D8B4E2" },
  { name: "User 3", value: 1, color: "#A8E6CF" },
  { name: "User 4", value: 1, color: "#FFD3B6" },
  { name: "User 5", value: 1, color: "#FFC3A0" },
  { name: "User 6", value: 1, color: "#FF8B94" },
];

const TASKS = [
  { id: "TASK #1", type: "3D MODEL · EQUIP", area: "UNIT 1", owner: "User 1", status: "IN PROGRESS", priority: "MEDIUM", progress: 30 },
  { id: "TASK #2", type: "3D MODEL · PIPING", area: "UNIT 1", owner: "User 2", status: "NOT STARTED", priority: "URGENT", progress: 60 },
  { id: "TASK #3", type: "3D MODEL · TIE-IN", area: "UNIT 2", owner: "User 3", status: "DONE", priority: "LOW", progress: 100 },
  { id: "TASK #4", type: "3D MODEL · AS-BUILT", area: "UNIT 2", owner: "User 4", status: "ON HOLD", priority: "LOW", progress: 0 },
  { id: "TASK #5", type: "ORTHO", area: "GENERAL", owner: "User 6", status: "DEPRIORITIZED", priority: "MEDIUM", progress: 0 },
  { id: "TASK #6", type: "2D DETAIL", area: "GENERAL", owner: "User 5", status: "REVIEW", priority: "URGENT", progress: 0 },
];

const PRIORITY_COLORS = { LOW: "#A8E6CF", MEDIUM: "#FFD3B6", URGENT: "#FF8B94" };

const Chip = ({ label, color }) => (
  <span
    className="inline-block whitespace-nowrap rounded-full px-2.5 py-0.5 text-[9px] font-bold tracking-wide"
    style={{ backgroundColor: color, color: "#050505" }}
  >
    {label}
  </span>
);

export default function DashboardMockup() {
  return (
    <div
      className="relative rounded-3xl border border-white/10 bg-[#0A120D]/80 p-5 shadow-[0_40px_120px_rgba(0,0,0,0.6)] backdrop-blur-2xl"
      data-testid="dashboard-mockup"
    >
      <div
        className="pointer-events-none absolute -inset-px rounded-3xl border border-[#00FF66]/20"
        aria-hidden="true"
      />

      <div className="mb-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            <span className="h-2.5 w-2.5 rounded-full bg-[#FF8B94]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#FFD3B6]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#A8E6CF]" />
          </div>
          <span className="font-display text-xs font-bold uppercase tracking-[0.24em] text-white/70">
            Vektor / Task Tracker
          </span>
        </div>
        <span
          className="rounded-full border border-[#00FF66]/40 bg-[#00FF66]/10 px-3 py-1 text-[10px] font-bold text-[#00FF66]"
          data-testid="mockup-total-tasks"
        >
          6 TOTAL TASKS
        </span>
      </div>

      <div className="mb-5 grid grid-cols-2 gap-4">
        <div className="rounded-2xl border border-white/5 bg-white/[0.03] p-3">
          <p className="mb-1 text-[9px] font-bold uppercase tracking-[0.2em] text-white/40">
            Status breakdown
          </p>
          <div className="h-36">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={DONUT_DATA}
                  dataKey="value"
                  innerRadius="62%"
                  outerRadius="88%"
                  strokeWidth={0}
                  paddingAngle={3}
                >
                  {DONUT_DATA.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl border border-white/5 bg-white/[0.03] p-3">
          <p className="mb-1 text-[9px] font-bold uppercase tracking-[0.2em] text-white/40">
            Assignment summary
          </p>
          <div className="h-36">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={OWNERS} layout="vertical" margin={{ top: 0, right: 8, bottom: 0, left: 0 }}>
                <XAxis type="number" hide domain={[0, 1]} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={86}
                  tick={{ fill: "rgba(255,255,255,0.45)", fontSize: 9 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} barSize={9}>
                  {OWNERS.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-white/5">
        <table className="w-full min-w-[560px] border-collapse text-left">
          <thead>
            <tr className="bg-[#2A1B38]">
              {["Task", "Document type", "Area", "Owner", "Status", "Priority", "Progress"].map((h) => (
                <th
                  key={h}
                  className="px-3 py-2.5 text-[9px] font-bold uppercase tracking-[0.16em] text-white/80"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {TASKS.map((task, i) => (
              <motion.tr
                key={task.id}
                initial={{ opacity: 0, x: -14 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 1.4 + i * 0.1 }}
                className="border-t border-white/5 bg-white/[0.015] transition-colors duration-200 hover:bg-white/[0.05]"
              >
                <td className="px-3 py-2.5 text-[10px] font-bold text-white/80">{task.id}</td>
                <td className="px-3 py-2.5 text-[10px] text-white/50">{task.type}</td>
                <td className="px-3 py-2.5">
                  <Chip label={task.area} color="#FFF3A6" />
                </td>
                <td className="px-3 py-2.5 text-[10px] text-white/60">{task.owner}</td>
                <td className="px-3 py-2.5">
                  <Chip label={task.status} color={STATUS_COLORS[task.status]} />
                </td>
                <td className="px-3 py-2.5">
                  <Chip label={task.priority} color={PRIORITY_COLORS[task.priority]} />
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 w-14 overflow-hidden rounded-full bg-white/10">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ backgroundColor: STATUS_COLORS[task.status] }}
                        initial={{ width: 0 }}
                        animate={{ width: `${task.progress}%` }}
                        transition={{ duration: 1.1, delay: 1.8 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                      />
                    </div>
                    <span className="text-[9px] font-semibold text-white/40">{task.progress}%</span>
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
